// Ses yönetim sistemi
type SoundStyle = 'whatsapp' | 'msn' | 'messenger' | 'custom';

class SoundManager {
  private audioContext: AudioContext | null = null;
  private isEnabled: boolean = true;
  private volume: number = 0.5;
  private userInteracted: boolean = false;
  private typingSoundEnabled: boolean = true;
  private lastTypingSound: number = 0;
  private pendingSound: (() => void) | null = null;
  private soundStyle: SoundStyle = 'whatsapp';
  private customSoundUrl: string | null = null;

  constructor() {
    this.initializeAudioContext();
    this.setupUserInteractionListener();
  }

  private setupUserInteractionListener() {
    const enableAudio = () => {
      this.userInteracted = true;
      const resume = this.audioContext && this.audioContext.state === 'suspended'
        ? this.audioContext.resume()
        : Promise.resolve();
      resume.then(() => {
        if (this.pendingSound) {
          const fn = this.pendingSound;
          this.pendingSound = null;
          fn();
        }
      });
    };

    ['click', 'touchstart', 'keydown', 'mousedown', 'pointerdown'].forEach(event => {
      document.addEventListener(event, enableAudio, { once: true });
    });

    window.addEventListener('focus', enableAudio, { once: true });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') enableAudio();
    }, { once: true });
  }

  private initializeAudioContext() {
    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch (error) {
      console.warn('AudioContext not supported:', error);
    }
  }

  // WhatsApp tarzı 3-nota yükselen bildirim sesi
  private playWhatsAppDing() {
    if (!this.audioContext) return;
    const ctx = this.audioContext;
    const now = ctx.currentTime;

    // 3 nota yükselen melodi: ~F#5 → D6 → F6
    const notes = [932, 1175, 1397];
    notes.forEach((freq, i) => {
      const t = now + i * 0.075;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, t);
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(this.volume * 0.42, t + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.22);
      osc.start(t);
      osc.stop(t + 0.23);
    });
  }

  // MSN Messenger tarzı 2-nota bildirim sesi
  private playMSNDing() {
    if (!this.audioContext) return;
    const ctx = this.audioContext;
    const now = ctx.currentTime;

    // İki nota: D5 (587Hz) → A5 (880Hz) — MSN'in klasik yükselen "ping"i
    const notes = [
      { freq: 587, delay: 0,    vol: 0.48, decay: 0.38 },
      { freq: 880, delay: 0.14, vol: 0.55, decay: 0.44 },
    ];

    notes.forEach(({ freq, delay, vol, decay }) => {
      const t = now + delay;

      // Ana sine dalga
      const osc1 = ctx.createOscillator();
      const gain = ctx.createGain();
      osc1.connect(gain);
      gain.connect(ctx.destination);
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(freq, t);

      // İnce harmonik (triangle, oktav yukarı) — MSN'in hafif metalik tınısı
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.type = 'triangle';
      osc2.frequency.setValueAtTime(freq * 2, t);

      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(this.volume * vol, t + 0.006);
      gain.gain.exponentialRampToValueAtTime(0.001, t + decay);

      gain2.gain.setValueAtTime(0, t);
      gain2.gain.linearRampToValueAtTime(this.volume * 0.08, t + 0.006);
      gain2.gain.exponentialRampToValueAtTime(0.001, t + decay * 0.6);

      osc1.start(t); osc1.stop(t + decay + 0.01);
      osc2.start(t); osc2.stop(t + decay + 0.01);
    });
  }

  // Facebook Messenger tarzı bildirim sesi (orijinal)
  private playMessengerDing() {
    if (!this.audioContext) return;
    const ctx = this.audioContext;
    const now = ctx.currentTime;

    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(880, now);
    osc1.frequency.exponentialRampToValueAtTime(660, now + 0.12);
    gain1.gain.setValueAtTime(0, now);
    gain1.gain.linearRampToValueAtTime(this.volume * 0.5, now + 0.008);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.28);
    osc1.start(now);
    osc1.stop(now + 0.3);

    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(1760, now);
    osc2.frequency.exponentialRampToValueAtTime(1320, now + 0.1);
    gain2.gain.setValueAtTime(0, now);
    gain2.gain.linearRampToValueAtTime(this.volume * 0.15, now + 0.008);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
    osc2.start(now);
    osc2.stop(now + 0.2);
  }

  private playDing() {
    if (this.soundStyle === 'custom' && this.customSoundUrl) {
      this.playCustomSound(this.customSoundUrl);
      return;
    }
    switch (this.soundStyle) {
      case 'whatsapp':  this.playWhatsAppDing();  break;
      case 'msn':       this.playMSNDing();        break;
      default:          this.playMessengerDing();  break;
    }
  }

  private playCustomSound(url: string) {
    const audio = new Audio(url);
    audio.volume = this.volume;
    audio.play().catch(() => {
      // Fallback to synthesized sound
      this.playWhatsAppDing();
    });
  }

  async playMessageNotification() {
    if (!this.isEnabled || !this.audioContext) return;

    try {
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
      if (this.audioContext.state === 'running') {
        this.userInteracted = true;
        this.pendingSound = null;
        this.playDing();
      } else if (!this.userInteracted) {
        this.pendingSound = () => this.playMessageNotification();
      }
    } catch (error) {
      if (!this.userInteracted) {
        this.pendingSound = () => this.playMessageNotification();
      }
    }
  }

  async playGroupNotification() {
    if (!this.isEnabled || !this.audioContext) return;

    try {
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
      if (this.audioContext.state === 'running') {
        this.userInteracted = true;
        this.pendingSound = null;
        // Grup için: WhatsApp ve MSN'de çift ding, Messenger'da da çift
        this.playDing();
        setTimeout(() => this.playDing(), 180);
      } else if (!this.userInteracted) {
        this.pendingSound = () => this.playGroupNotification();
      }
    } catch (error) {
      if (!this.userInteracted) {
        this.pendingSound = () => this.playGroupNotification();
      }
    }
  }

  // WhatsApp tarzı klavye yazma sesi
  playTypingSound() {
    if (!this.typingSoundEnabled || !this.audioContext || !this.userInteracted) return;

    const now = Date.now();
    if (now - this.lastTypingSound < 40) return;
    this.lastTypingSound = now;

    const ctx = this.audioContext;
    const t = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(400, t);
    osc.frequency.exponentialRampToValueAtTime(120, t + 0.018);
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(this.volume * 0.12, t + 0.003);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.022);
    osc.start(t);
    osc.stop(t + 0.025);
  }

  setEnabled(enabled: boolean) {
    this.isEnabled = enabled;
    localStorage.setItem('sound_notifications_enabled', enabled.toString());
  }

  setVolume(volume: number) {
    this.volume = Math.max(0, Math.min(1, volume));
    localStorage.setItem('sound_volume', this.volume.toString());
  }

  setTypingSoundEnabled(enabled: boolean) {
    this.typingSoundEnabled = enabled;
    localStorage.setItem('typing_sound_enabled', enabled.toString());
  }

  setSoundStyle(style: SoundStyle) {
    this.soundStyle = style;
    localStorage.setItem('sound_style', style);
  }

  setCustomSoundUrl(url: string | null) {
    this.customSoundUrl = url;
    if (url) {
      this.soundStyle = 'custom';
      localStorage.setItem('sound_style', 'custom');
      localStorage.setItem('sound_custom_url', url);
    } else {
      if (this.soundStyle === 'custom') {
        this.soundStyle = 'whatsapp';
        localStorage.setItem('sound_style', 'whatsapp');
      }
      localStorage.removeItem('sound_custom_url');
    }
  }

  loadSettings() {
    const enabled = localStorage.getItem('sound_notifications_enabled');
    const volume = localStorage.getItem('sound_volume');
    const typingEnabled = localStorage.getItem('typing_sound_enabled');
    const style = localStorage.getItem('sound_style') as SoundStyle | null;
    const customUrl = localStorage.getItem('sound_custom_url');

    if (enabled !== null) this.isEnabled = enabled === 'true';
    if (volume !== null) this.volume = parseFloat(volume);
    if (typingEnabled !== null) this.typingSoundEnabled = typingEnabled === 'true';
    if (style !== null) this.soundStyle = style;
    if (customUrl !== null) this.customSoundUrl = customUrl;
  }

  async testSound() {
    await this.playMessageNotification();
  }

  async initialize() {
    if (!this.audioContext) {
      this.initializeAudioContext();
    }
    if (this.audioContext && this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
        this.userInteracted = true;
      } catch (error) {
        console.warn('⚠️ Failed to initialize sound system:', error);
      }
    }
  }

  getSettings() {
    return {
      enabled: this.isEnabled,
      volume: this.volume,
      typingSoundEnabled: this.typingSoundEnabled,
      soundStyle: this.soundStyle,
      customSoundUrl: this.customSoundUrl,
      userInteracted: this.userInteracted,
      contextState: this.audioContext?.state
    };
  }
}

export const soundManager = new SoundManager();
soundManager.loadSettings();
